import React, { useEffect } from "react";
import {
	Button,
	Checkbox,
	CheckboxGroup,
	Icon,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	useCheckboxGroup,
	useRadioGroup,
	Radio,
} from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
	FaArrowRight,
	FaArrowLeft,
	FaRegMoneyBillAlt,
	FaUsers,
} from "react-icons/fa";
import { format } from "date-fns";
import {
	formatAsCurrency,
	formatContractJobAdDuration,
	scrollToTop,
} from "src/utlis/helpers";
import { logEvent } from "src/utlis/analytics";
import { GET_EMPLOYER_JOB_ADS } from "src/features/jobs/graphql";
import { GrLocation } from "react-icons/gr";
import { RiBuildingLine } from "react-icons/ri";
import { MdOutlineBusinessCenter } from "react-icons/md";
import { IoCheckmarkCircle } from "react-icons/io5";
import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from "@chakra-ui/icons";
import {
	EmploymentType,
	GetEmployerJobAdsQueryVariables,
} from "src/__generated__/graphql";
import { Tags } from "lucide-react";
import { RxCounterClockwiseClock } from "react-icons/rx";
import { Loading } from "src/components/Loading";
import { RequestFailed } from "src/components/RequestFailed";

const dateFilterOptions = [
	{
		value: "ALL_TIME",
		label: "All time",
	},
	{
		value: "YESTERDAY",
		label: "Yesterday",
	},
	{
		value: "LAST_WEEK",
		label: "Last week",
	},
	{
		value: "LAST_MONTH",
		label: "Last month",
	},
];

const employmentTypeOptions = [
	{
		value: "FULL_TIME",
		label: "Full Time",
	},
	{
		value: "CONTRACT",
		label: "Contract",
	},
];

const defaultFilters: {
	dateFilter: GetEmployerJobAdsQueryVariables["dateFilter"];
	employmentTypes: EmploymentType[];
} = {
	dateFilter: null,
	employmentTypes: [],
};

export default function LaborHackJobs() {
	const navigate = useNavigate();
	const [currentPage, setCurrentPage] = React.useState<number>(1);
	const [selectedJobId, setSelectedJobId] = React.useState<string | null>(null);

	const [dateFilter, setDateFilter] =
		React.useState<GetEmployerJobAdsQueryVariables["dateFilter"]>(null);

	const [employmentTypesFilter, setEmploymentTypesFilter] = React.useState<
		Array<EmploymentType>
	>([]);

	const [dateFiltersOpen, setDateFiltersOpen] = React.useState(false);
	const [employmentTypesOpen, setEmploymentTypesOpen] = React.useState(false);

	const { getRadioProps: getDateFilterRadioProps } = useRadioGroup({
		defaultValue: dateFilter || undefined,
		value: dateFilter || undefined,
		onChange: (value) => {
			setDateFilter(value as GetEmployerJobAdsQueryVariables["dateFilter"]);
		},
	});

	const {
		getCheckboxProps: getEmploymentTypeCheckboxProps,
		value: employmentTypesValue,
	} = useCheckboxGroup({
		defaultValue: employmentTypesFilter,
		value: employmentTypesFilter,
		onChange: (values) => {
			setEmploymentTypesFilter(values as Array<EmploymentType>);
		},
	});

	useEffect(() => {
		scrollToTop();
	}, [currentPage]);

	const jobsPerPage = 6;

	const { data, loading, error } = useQuery(GET_EMPLOYER_JOB_ADS, {
		variables: {
			dateFilter,
			employmentTypes: employmentTypesFilter,
		},
	});

	const jobs = data?.getEmployerJobAds;

	const totalPages = (jobs && Math.ceil(jobs.length / jobsPerPage)) || 0;

	const paginatedJobs =
		(!loading &&
			jobs &&
			jobs.slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage)) ||
		[];

	const handleNextPage = () => {
		if (currentPage < totalPages) {
			setCurrentPage((prev) => prev + 1);
		}
	};

	const handlePrevPage = () => {
		if (currentPage > 1) {
			setCurrentPage((prev) => prev - 1);
		}
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<Loading />
			</div>
		);
	}

	if (error) {
		return <RequestFailed text={error.message} />;
	}

	return (
		<>
			<Helmet>
				<title>LaborHack | LaborHack Jobs</title>
			</Helmet>
			<div className="w-full pb-8">
				<div className="w-full flex flex-col gap-4 lg:gap-6">
					<div
						className={`w-full border-[#ACACAC] border-y-[1px] flex flex-col lg:flex-row gap-5 lg:gap-8 lg:items-center py-4`}
					>
						<div>
							<p className="text-[#6E6E6E] text-[16px] lg:text-[20px]">
								Filters
							</p>
						</div>
						<div className="flex gap-4">
							<Menu
								matchWidth={false}
								isOpen={dateFiltersOpen}
								onClose={() => setDateFiltersOpen(false)}
								closeOnSelect={false}
							>
								{({ isOpen, onClose }) => (
									<>
										<MenuButton
											as={Button}
											rightIcon={
												isOpen ? (
													<ChevronUpIcon w={6} h={6} />
												) : (
													<ChevronDownIcon w={6} h={6} />
												)
											}
											w={{
												base: "45%",
												lg: "180px",
											}}
											h={{
												base: "40px",
												lg: "56px",
											}}
											border={"1px solid #858585"}
											fontSize={{
												base: "15px",
												lg: "18px",
											}}
											textColor={"#212121"}
											backgroundColor={"#FEFEFE"}
											onClick={() => {
												setDateFiltersOpen(!isOpen);
											}}
										>
											Date posted
										</MenuButton>
										<MenuList
											rounded={0}
											w={{
												base: "260px",
												lg: "547px",
											}}
										>
											<>
												<div className="flex justify-between items-center px-3 pb-3 border-b-[1px] border-[#D8D8D8]">
													<p className="text-[#464646] text-[16px] lg:text-[22px]">
														Date posted
													</p>
													<IconButton
														aria-label="close"
														variant={"outline"}
														rounded={"100%"}
														border={0}
														color={"#212121"}
														onClick={() => onClose()}
														icon={<Icon as={CloseIcon} w={3} h={3} />}
													/>
												</div>
												<div className="py-4 border-b-[1px] border-[#D8D8D8]">
													{dateFilterOptions.map((option) => (
														<MenuItem key={option.value}>
															{/* @ts-expect-error Chakra v2 type mismatch between useRadioGroup.getRadioProps (HTMLInputElement) and Radio (HTMLDivElement) */}
															<Radio
																{...getDateFilterRadioProps({
																	value: option.value,
																})}
																isChecked={dateFilter === option.value}
																size={"lg"}
																fontSize={{
																	base: "16px",
																	lg: "22px",
																}}
															>
																{option.label}
															</Radio>
														</MenuItem>
													))}
												</div>
												<div className="flex gap-6 justify-end px-3 py-4">
													<Button
														colorScheme="flatteredFlamingo"
														variant={"unstyled"}
														textDecoration={"underline"}
														h={{
															base: "38px",
															lg: "49px",
														}}
														fontSize={{
															base: "14px",
															lg: "16px",
														}}
														onClick={() => {
															setDateFilter(defaultFilters.dateFilter);
															onClose();
														}}
													>
														Clear filter
													</Button>
													<Button
														colorScheme="flatteredFlamingo"
														h={{
															base: "38px",
															lg: "49px",
														}}
														fontSize={{
															base: "16px",
															lg: "20px",
														}}
														onClick={() => {
															onClose();
														}}
													>
														Apply filter
													</Button>
												</div>
											</>
										</MenuList>
									</>
								)}
							</Menu>

							<Menu
								isOpen={employmentTypesOpen}
								onClose={() => setEmploymentTypesOpen(false)}
								closeOnSelect={false}
							>
								{({ isOpen, onClose }) => (
									<>
										<MenuButton
											as={Button}
											rightIcon={
												isOpen ? (
													<ChevronUpIcon w={6} h={6} />
												) : (
													<ChevronDownIcon w={6} h={6} />
												)
											}
											w={{
												base: "38%",
												lg: "140px",
											}}
											h={{
												base: "40px",
												lg: "56px",
											}}
											border={"1px solid #858585"}
											fontSize={{
												base: "15px",
												lg: "18px",
											}}
											textColor={"#212121"}
											backgroundColor={"#FEFEFE"}
											onClick={() => {
												setEmploymentTypesOpen(!isOpen);
											}}
										>
											Job type
										</MenuButton>
										<MenuList
											rounded={0}
											w={{
												base: "260px",
												lg: "547px",
											}}
										>
											<>
												<div className="flex justify-between items-center px-3 pb-3 border-b-[1px] border-[#D8D8D8]">
													<p className="text-[#464646] text-[16px] lg:text-[22px]">
														Job type
													</p>
													<IconButton
														aria-label="close"
														variant={"outline"}
														rounded={"100%"}
														border={0}
														color={"#212121"}
														onClick={() => onClose()}
														icon={<Icon as={CloseIcon} w={3} h={3} />}
													/>
												</div>
												<div className="py-4 border-b-[1px] border-[#D8D8D8]">
													<CheckboxGroup value={employmentTypesValue}>
														{employmentTypeOptions.map((option) => (
															<MenuItem key={option.value}>
																<Checkbox
																	{...getEmploymentTypeCheckboxProps({
																		value: option.value,
																	})}
																	isChecked={employmentTypesFilter.includes(
																		option.value as EmploymentType
																	)}
																	size={"lg"}
																	fontSize={{
																		base: "16px",
																		lg: "22px",
																	}}
																>
																	{option.label}
																</Checkbox>
															</MenuItem>
														))}
													</CheckboxGroup>
												</div>
												<div className="flex gap-6 justify-end px-3 py-4">
													<Button
														colorScheme="flatteredFlamingo"
														variant={"unstyled"}
														textDecoration={"underline"}
														h={{
															base: "38px",
															lg: "49px",
														}}
														fontSize={{
															base: "14px",
															lg: "16px",
														}}
														onClick={() => {
															setEmploymentTypesFilter(
																defaultFilters.employmentTypes
															);
															onClose();
														}}
													>
														Clear filter
													</Button>
													<Button
														colorScheme="flatteredFlamingo"
														h={{
															base: "38px",
															lg: "49px",
														}}
														fontSize={{
															base: "16px",
															lg: "20px",
														}}
														onClick={() => {
															onClose();
														}}
													>
														Apply filter
													</Button>
												</div>
											</>
										</MenuList>
									</>
								)}
							</Menu>
						</div>
					</div>
					<div className="w-full">
						<h2
							className="text-[18px] lg:text-[24px] font-medium text-[#122A38]"
							id="jobs-header"
						>
							Jobs
						</h2>
						<div className="grid grid-cols-1 lg:grid-cols-10 gap-6 mt-4">
							<div
								className={`flex flex-col gap-4 w-full h-full ${
									!selectedJobId
										? "col-span-7"
										: "hidden lg:block lg:col-span-5"
								}`}
							>
								{!loading && paginatedJobs.length > 0 ? (
									paginatedJobs.map((job, idx) => (
										<div
											className={`w-full ${
												idx !== 0 ? "mt-3 lg:mt-7" : ""
											} cursor-pointer`}
											key={job.id}
											onClick={() => {
												setSelectedJobId(job.id);
												scrollToTop();
											}}
										>
											<div
												className={`border p-6 ${
													selectedJobId === job.id
														? "border-[#ED6E5C] bg-[#FFF9F8]"
														: "border-[#ACACAC] hover:border-[#ED6E5C] hover:bg-[#FEFEFE]"
												} transition-colors duration-200`}
											>
												<div className="flex flex-col lg:flex-row gap-4 lg:justify-between lg:items-start">
													<div className="flex flex-col gap-2">
														<h3 className="text-[#0E212D] text-[18px] lg:text-[23px] font-medium">
															{job.title}
														</h3>
														<p className="text-[#212121] text-[14px] lg:text-[18px] font-normal">
															{job.companyName}
														</p>
														<p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
															Date posted:{" "}
															<span className="text-[#000000]">
																{format(new Date(job.createdAt), "PPP")}
															</span>
														</p>
														{job.visibility === "PRIVATE" && (
															<div className="flex items-center gap-2">
																<p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
																	Exclusive to{" "}
																	<span className="text-[#000000]">
																		{job.organization.name}
																	</span>{" "}
																	members
																</p>
																<FaUsers className="text-[#6E6E6E]" size={16} />
															</div>
														)}
													</div>
												</div>
											</div>
										</div>
									))
								) : (
									<div className="flex items-center justify-center h-full p-4 lg:p-8 border-[1px] border-[#ACACAC]">
										<p className="text-[#0E212D] text-lg lg:text-xl">
											{!!dateFilter || employmentTypesFilter.length > 0
												? "No jobs match this filter"
												: "No jobs available at the moment, please check back later."}
										</p>
									</div>
								)}
							</div>

							{/* Job Details Panel */}
							{selectedJobId && jobs && (
								<div
									className={`p-3 w-full border-[#ACACAC] border-[1px] ${
										!selectedJobId
											? "hidden"
											: " block col-span-1 lg:col-span-5"
									}`}
								>
									{(() => {
										const selectedJob = jobs.find(
											(job) => job.id === selectedJobId
										);
										if (!selectedJob) return null;

										return (
											<div className="w-full">
												<div className="block lg:hidden mb-4">
													<IconButton
														aria-label="arrow left"
														variant={"outline"}
														rounded={"100%"}
														borderColor={"#ACACAC"}
														color={"#0E212D"}
														onClick={() => {
															setSelectedJobId(null);
														}}
														icon={<Icon as={FaArrowLeft} w={4} h={4} />}
													/>
												</div>
												<div className="border-[#ACACAC] border-b-[1px] pb-6 lg:pb-8 flex flex-col lg:flex-row gap-3 lg:justify-between lg:items-end">
													<div className="w-full flex flex-col gap-3 lg:gap-5">
														<div className="w-[85%] lg:w-[55%]">
															<h3 className="text-[#0E212D] text-[18px] lg:text-[23px] font-medium">
																{selectedJob.title}
															</h3>
														</div>
														<div className="flex flex-col gap-2">
															<p className="text-[#212121] text-[14px] lg:text-[18px] font-normal">
																{selectedJob.companyName}
															</p>
															<p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
																Date posted:{" "}
																<span className="text-[#000000]">
																	{format(
																		new Date(selectedJob.createdAt),
																		"PPP"
																	)}
																</span>
															</p>
															{selectedJob.applicationDeadline && (
																<div className="flex items-center gap-2">
																	<p className="text-[#8d524a] text-[14px] lg:text-[18px]">
																		Application deadline:{" "}
																		<span className="text-[#883227]">
																			{format(
																				new Date(
																					selectedJob.applicationDeadline
																				),
																				"PPP"
																			)}
																		</span>
																	</p>
																</div>
															)}
															{selectedJob.visibility === "PRIVATE" && (
																<div className="flex items-center gap-2">
																	<p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
																		Exclusive to{" "}
																		<span className="text-[#000000]">
																			{selectedJob.organization.name}
																		</span>{" "}
																		members
																	</p>
																	<FaUsers
																		className="text-[#6E6E6E]"
																		size={16}
																	/>
																</div>
															)}
														</div>
													</div>
													<div className="flex flex-col lg:flex-row gap-3">
														<Button
															bgColor={
																selectedJob.hasApplied && !selectedJob.canApply
																	? "#FBE2DE"
																	: "#ED6E5C"
															}
															color={
																selectedJob.hasApplied && !selectedJob.canApply
																	? "#F08677"
																	: "#FEFEFE"
															}
															h={{
																base: "40px",
																lg: "57px",
															}}
															fontSize={{
																base: "16px",
																lg: "20px",
															}}
															rightIcon={
																<Icon
																	as={
																		selectedJob.hasApplied &&
																		!selectedJob.canApply
																			? IoCheckmarkCircle
																			: FaArrowRight
																	}
																	fill={
																		selectedJob.hasApplied &&
																		!selectedJob.canApply
																			? "#F08677"
																			: "#FEFEFE"
																	}
																	w={{
																		base: 4,
																		lg: 5,
																	}}
																	h={{
																		base: 4,
																		lg: 5,
																	}}
																	ml={0}
																	fontWeight={"500"}
																/>
															}
															isDisabled={
																selectedJob.hasApplied && !selectedJob.canApply
															}
															onClick={() => {
																logEvent(
																	"job_application_started_from_job_board",
																	{
																		job_id: selectedJob.id,
																		job_title: selectedJob.title,
																	}
																);

																navigate(
																	`/opportunities/laborhack/${selectedJob.id}/apply`
																);
															}}
														>
															{selectedJob.hasApplied && !selectedJob.canApply
																? "Applied"
																: "Apply"}
														</Button>
													</div>
												</div>
												<div className="border-[#ACACAC] border-b-[1px] py-6 lg:py-8">
													<div
														className="markdown"
														dangerouslySetInnerHTML={{
															__html: selectedJob.description,
														}}
													/>
												</div>
												<div className="grid grid-cols-1 lg:grid-cols-[2fr,2fr] gap-6 lg:gap-8 py-6 lg:py-8">
													<div className="flex items-center gap-4">
														<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
															<Icon
																as={GrLocation}
																w={{
																	base: "20px",
																	lg: "20px",
																}}
																h={{
																	base: "20px",
																	lg: "20px",
																}}
																color={"#212121"}
															/>
														</div>
														<div className="flex flex-col gap-2">
															<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
																Location
															</p>
															<p className="text-[#212121] text-[16px] lg:text-[20px]">
																{`${selectedJob.location.area}, ${selectedJob.location.state}, ${selectedJob.location.country}`}
															</p>
														</div>
													</div>

													<div className="flex items-center gap-4">
														<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
															<Icon
																as={MdOutlineBusinessCenter}
																w={{
																	base: "20px",
																	lg: "20px",
																}}
																h={{
																	base: "20px",
																	lg: "20px",
																}}
																color={"#212121"}
															/>
														</div>
														<div className="flex flex-col gap-2">
															<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
																Employment type
															</p>
															<p className="text-[#212121] text-[16px] lg:text-[20px]">
																{`${employmentTypeToText(
																	selectedJob.employmentType
																)}`}
															</p>
														</div>
													</div>

													{selectedJob.duration && (
														<div className="flex items-center gap-4">
															<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
																<Icon
																	as={RxCounterClockwiseClock}
																	w={{
																		base: "20px",
																		lg: "20px",
																	}}
																	h={{
																		base: "20px",
																		lg: "20px",
																	}}
																	color={"#212121"}
																/>
															</div>
															<div className="flex flex-col gap-2">
																<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
																	Duration
																</p>
																<p className="text-[#212121] text-[16px] lg:text-[20px]">
																	{formatContractJobAdDuration(
																		selectedJob.duration
																	)}
																</p>
															</div>
														</div>
													)}

													{selectedJob.salaryDetails && (
														<div className="flex items-center gap-4">
															<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
																<Icon
																	as={FaRegMoneyBillAlt}
																	w={{
																		base: "20px",
																		lg: "20px",
																	}}
																	h={{
																		base: "20px",
																		lg: "20px",
																	}}
																	color={"#212121"}
																/>
															</div>
															<div className="flex flex-col gap-2">
																<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
																	{`Salary ${
																		selectedJob.salaryDetails.cap ? "range" : ""
																	}`}
																</p>
																<p className="text-[#212121] text-[16px] lg:text-[20px]">
																	{selectedJob.salaryDetails.starting &&
																		formatAsCurrency(
																			selectedJob.salaryDetails.starting,
																			selectedJob.salaryDetails.currencyCode
																		)}{" "}
																	{selectedJob.salaryDetails.cap
																		? `- ${formatAsCurrency(
																				selectedJob.salaryDetails.cap,
																				selectedJob.salaryDetails.currencyCode
																		  )}`
																		: ""}
																</p>
															</div>
														</div>
													)}

													<div className="flex items-center gap-4">
														<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
															<Icon
																as={RiBuildingLine}
																w={{
																	base: "20px",
																	lg: "20px",
																}}
																h={{
																	base: "20px",
																	lg: "20px",
																}}
																color={"#212121"}
															/>
														</div>
														<div className="flex flex-col gap-2">
															<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
																Employer
															</p>
															<p className="text-[#212121] text-[16px] lg:text-[20px]">
																{selectedJob.companyName}
															</p>
														</div>
													</div>

													{selectedJob.tags && selectedJob.tags.length > 0 ? (
														<div className="flex items-start gap-4">
															<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
																<Icon
																	as={Tags}
																	w={{
																		base: "20px",
																		lg: "20px",
																	}}
																	h={{
																		base: "20px",
																		lg: "20px",
																	}}
																	color={"#212121"}
																/>
															</div>
															<div className="flex flex-col gap-2">
																<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
																	Tags
																</p>
																<p className="text-[#212121] text-[16px] lg:text-[20px]">
																	{selectedJob.tags
																		.map((tag) => tag.name)
																		.join(", ")}
																</p>
															</div>
														</div>
													) : (
														selectedJob.categories &&
														selectedJob.categories.length > 0 && (
															<div className="flex items-start gap-4">
																<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
																	<Icon
																		as={Tags}
																		w={{
																			base: "20px",
																			lg: "20px",
																		}}
																		h={{
																			base: "20px",
																			lg: "20px",
																		}}
																		color={"#212121"}
																	/>
																</div>
																<div className="flex flex-col gap-2">
																	<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
																		{selectedJob.categories.length > 1
																			? "Categories"
																			: "Category"}
																	</p>
																	<p className="text-[#212121] text-[16px] lg:text-[20px]">
																		{selectedJob.categories
																			.map((category) => category.name)
																			.join(", ")}
																	</p>
																</div>
															</div>
														)
													)}
												</div>
											</div>
										);
									})()}
								</div>
							)}
						</div>
						{/* Pagination */}
						<div className="flex gap-4 py-4 w-full lg:w-[30%] justify-end lg:justify-start">
							<Button
								backgroundColor={"#FEFEFE"}
								color={"#0E1E27"}
								border={"1px solid #121212"}
								paddingY={{
									base: 4,
									lg: 6,
								}}
								w={"40%"}
								fontSize={{
									base: 15,
									lg: 18,
								}}
								onClick={handlePrevPage}
								isDisabled={currentPage === 1 || totalPages === 0}
							>
								Prev
							</Button>
							<Button
								backgroundColor={"#FEFEFE"}
								color={"#0E1E27"}
								border={"1px solid #121212"}
								paddingY={{
									base: 4,
									lg: 6,
								}}
								w={"40%"}
								fontSize={{
									base: 15,
									lg: 18,
								}}
								onClick={handleNextPage}
								isDisabled={currentPage === totalPages}
							>
								Next
							</Button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

const employmentTypeToText = (type: string) => {
	switch (type) {
		case "FULL_TIME":
			return "Full Time";
		case "PART_TIME":
			return "Part Time";
		case "CONTRACT":
			return "Contract";
		default:
			return "Unknown";
	}
};
